import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { 
  CloudArrowUpIcon, 
  CpuChipIcon, 
  CubeIcon, 
  LockClosedIcon, 
  ChartBarIcon, 
  ChatBubbleLeftRightIcon, 
  UserGroupIcon, 
  GlobeAltIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';

const CloudMind: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-[#333333]">
      <Helmet>
        <title>CloudMind Framework | Modèles LLM | Infinity Cloud</title>
        <meta name="description" content="Déploiement et gestion de modèles comme ChatGPT pour diverses applications professionnelles" />
      </Helmet>

      <style>
        {`
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0px); }
          }
          .float-animation {
            animation: float 6s ease-in-out infinite;
          }
        `}
      </style>

      {/* Hero Section */}
      <section className="pt-16 pb-8 overflow-hidden relative bg-white">
        <div 
          className="absolute inset-0 opacity-5 z-0" 
          style={{
            backgroundImage: `url('/images/kits/cloudmind-bg.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(30px)'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-3">
              <span 
                style={{
                  background: 'linear-gradient(135deg, #0066cc, #5ac8fa)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  paddingBottom: '0.15em',
                  lineHeight: 1.2
                }}
              >
                CloudMind Framework
              </span>
            </h1>
            <p className="text-2xl md:text-3xl font-light text-[#555] mb-4 max-w-3xl mx-auto">
              Révolutionnez la Gestion de Projets avec l'<span className="font-semibold text-blue-600">IA</span>
            </p>
            <p className="text-lg text-[#666666] max-w-2xl mx-auto mb-6">
              Automatisez vos projets avec une interface intuitive, des agents IA dynamiques et des outils intégrés pour une productivité maximale.
            </p>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="mt-6 flex justify-center"
            >
              <img 
                src="/images/kits/cloudmind-hero.png"
                alt="Placeholder DALL-E: Une salle de réunion futuriste avec des équipes analysant un tableau de bord holographique affichant des agents IA en action, ambiance technologique bleue et argentée, style cyberpunk" 
                className="w-full max-w-3xl rounded-2xl shadow-xl float-animation"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Section Fonctionnalités Clés */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <span 
              style={{
                background: 'linear-gradient(135deg, #0066cc, #5ac8fa)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
              className="text-xl font-medium inline-block mb-3"
            >
              Pourquoi Choisir CloudMind ?
            </span>
            <h2 className="text-4xl font-semibold mb-4 text-[#333333]">
              Fonctionnalités Clés
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-[#666666]">
              Une solution complète pour la gestion intelligente de vos projets grâce à l'IA.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Interface Intuitive */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0 }}
              className="rounded-[24px] shadow-lg hover:shadow-xl transition-shadow flex flex-col bg-white overflow-hidden"
              whileHover={{ y: -10 }}
            >
              <div className="p-5 flex items-center justify-center h-32" style={{ background: 'linear-gradient(135deg, #0066cc, #5ac8fa)' }}>
                <ChatBubbleLeftRightIcon className="h-16 w-16 text-white opacity-80"/>
              </div>
              <div className="p-5 flex-grow">
                <h3 className="text-xl font-semibold mb-2 mt-1">Interface Intuitive</h3>
                <p className="text-[#666666] flex-grow">Basée sur Streamlit, elle simplifie la gestion sans expertise technique.</p>
              </div>
              <div className="p-3 bg-gray-50 text-center">
                <img 
                  src="/images/kits/cloudmind-streamlit.png" 
                  alt="Placeholder DALL-E: Un tableau de bord Streamlit moderne affiché sur un écran tactile, montrant des graphiques de performance de projet, style minimaliste et technologique" 
                  className="w-full h-auto rounded-md shadow-sm"
                />
              </div>
            </motion.div>

            {/* Agents IA Dynamiques */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="rounded-[24px] shadow-lg hover:shadow-xl transition-shadow flex flex-col bg-white overflow-hidden"
              whileHover={{ y: -10 }}
            >
              <div className="p-5 flex items-center justify-center h-32" style={{ background: 'linear-gradient(135deg, #0066cc, #5ac8fa)' }}>
                <UserGroupIcon className="h-16 w-16 text-white opacity-80"/>
              </div>
              <div className="p-5 flex-grow">
                <h3 className="text-xl font-semibold mb-2 mt-1">Agents IA Dynamiques</h3>
                <p className="text-[#666666] flex-grow">Crée automatiquement des équipes d'agents IA adaptées à vos besoins.</p>
              </div>
              <div className="p-3 bg-gray-50 text-center">
                <img 
                  src="/images/kits/cloudmind-agents.png" 
                  alt="Placeholder DALL-E: Un réseau d'agents IA interconnectés représentés par des nœuds lumineux dans un espace numérique, style futuriste et holographique" 
                  className="w-full h-auto rounded-md shadow-sm"
                />
              </div>
            </motion.div>

            {/* Intégration d'Outils */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="rounded-[24px] shadow-lg hover:shadow-xl transition-shadow flex flex-col bg-white overflow-hidden"
              whileHover={{ y: -10 }}
            >
              <div className="p-5 flex items-center justify-center h-32" style={{ background: 'linear-gradient(135deg, #0066cc, #5ac8fa)' }}>
                <GlobeAltIcon className="h-16 w-16 text-white opacity-80"/>
              </div>
              <div className="p-5 flex-grow">
                <h3 className="text-xl font-semibold mb-2 mt-1">Intégration d'Outils</h3>
                <p className="text-[#666666] flex-grow">Accédez à des données en temps réel avec l'API Bing Search.</p>
              </div>
              <div className="p-3 bg-gray-50 text-center">
                <img 
                  src="/images/kits/cloudmind-bing.png" 
                  alt="Placeholder DALL-E: Un globe numérique affichant des flux de données en temps réel provenant de recherches Bing, ambiance high-tech" 
                  className="w-full h-auto rounded-md shadow-sm"
                />
              </div>
            </motion.div>

            {/* Scalabilité */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="rounded-[24px] shadow-lg hover:shadow-xl transition-shadow flex flex-col bg-white overflow-hidden"
              whileHover={{ y: -10 }}
            >
              <div className="p-5 flex items-center justify-center h-32" style={{ background: 'linear-gradient(135deg, #0066cc, #5ac8fa)' }}>
                <ChartBarIcon className="h-16 w-16 text-white opacity-80"/>
              </div>
              <div className="p-5 flex-grow">
                <h3 className="text-xl font-semibold mb-2 mt-1">Scalabilité</h3>
                <p className="text-[#666666] flex-grow">Gérez plusieurs projets avec une organisation sans faille.</p>
              </div>
              <div className="p-3 bg-gray-50 text-center">
                <img 
                  src="/images/kits/cloudmind-scalability.png" 
                  alt="Placeholder DALL-E: Une structure de dossiers numériques interconnectés dans un cloud, symbolisant l'organisation des projets, style cybernétique" 
                  className="w-full h-auto rounded-md shadow-sm"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section Aspects Techniques */}
      <section className="py-10 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <span 
              style={{
                background: 'linear-gradient(135deg, #0066cc, #5ac8fa)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
              className="text-xl font-medium inline-block mb-3"
            >
              Architecture Technique
            </span>
            <h2 className="text-4xl font-semibold mb-4 text-[#333333]">
              Comment CloudMind Fonctionne
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-[#666666]">
              Découvrez les éléments techniques qui permettent à CloudMind d'orchestrer vos équipes d'agents IA.
            </p>
          </motion.div>

          <div className="flex flex-col md:flex-row gap-8 items-center mb-10">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="md:w-1/2"
            >
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-2xl font-semibold mb-4 text-blue-600">Initiation de Projet</h3>
                <p className="text-[#666666] mb-4">Créez un mandat et un dossier dédié en quelques clics pour lancer votre projet.</p>
                <h4 className="text-lg font-medium mb-2 text-[#555]">Configuration</h4>
                <p className="text-[#666666] mb-2">Sélectionnez un fichier CSV pour générer des agents IA adaptés :</p>
                <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto mb-4">
                  <code className="text-[#333]">
                    def master_ai_create_group(prompt, csv_file_path, mandate_content):<br/>
                    &nbsp;&nbsp;master_model = ChatOpenAI(model="gpt-4o-mini", max_tokens=1500, temperature=0.7)
                  </code>
                </pre>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="md:w-1/2"
            >
              <img 
                src="/images/kits/cloudmind-technical.png"
                alt="Placeholder DALL-E: Un flux de code Python et un diagramme de workflow CloudMind dans un environnement numérique, style cybernétique avec néons bleus" 
                className="w-full rounded-lg shadow-lg"
              />
            </motion.div>
          </div>

          <div className="flex flex-col md:flex-row-reverse gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="md:w-1/2"
            >
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-2xl font-semibold mb-4 text-blue-600">Exécution & Organisation</h3>
                <h4 className="text-lg font-medium mb-2 text-[#555]">Exécution</h4>
                <p className="text-[#666666] mb-2">Lancez les agents individuellement ou en séquence :</p>
                <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto mb-4">
                  <code className="text-[#333]">
                    def execute_agent(agent_name, mandate_content, accumulated_output, project_folder):<br/>
                    &nbsp;&nbsp;messages = [SystemMessage(content=f"You are the {'{agent_name}'} Agent"), HumanMessage(content=prompt)]
                  </code>
                </pre>
                <h4 className="text-lg font-medium mb-2 text-[#555]">Organisation</h4>
                <p className="text-[#666666] mb-2">Structure robuste pour une gestion multi-projets :</p>
                <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                  <code className="text-[#333]">
                    sandbox_directory = '/data/Project_Zero_The_Origin/sandbox'<br/>
                    for directory in [projects_directory, mandate_directory, config_directory]:<br/>
                    &nbsp;&nbsp;os.makedirs(directory, exist_ok=True)
                  </code>
                </pre>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="md:w-1/2 flex justify-center"
            >
              <div className="grid grid-cols-2 gap-4 w-full max-w-md">
                {['Workflow Designer', 'Agent Manager', 'API Gateway', 'Data Connector'].map((title, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.1 * index }}
                    className="bg-white p-4 rounded-lg shadow-md text-center"
                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  >
                    <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center rounded-full" 
                      style={{ background: 'linear-gradient(135deg, #0066cc, #5ac8fa)' }}>
                      {index === 0 && <ChatBubbleLeftRightIcon className="h-6 w-6 text-white" />}
                      {index === 1 && <UserGroupIcon className="h-6 w-6 text-white" />}
                      {index === 2 && <CpuChipIcon className="h-6 w-6 text-white" />}
                      {index === 3 && <GlobeAltIcon className="h-6 w-6 text-white" />}
                    </div>
                    <h4 className="text-sm font-medium text-[#333]">{title}</h4>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section Étapes d'Adoption */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <span 
              style={{
                background: 'linear-gradient(135deg, #0066cc, #5ac8fa)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
              className="text-xl font-medium inline-block mb-3"
            >
              Commencez Maintenant
            </span>
            <h2 className="text-4xl font-semibold mb-4 text-[#333333]">
              Prêt à Transformer Vos Projets ?
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-[#666666]">
              Suivez ces étapes pour intégrer CloudMind dans votre flux de travail et booster votre productivité.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Déploiement Rapide */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0 }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <div className="w-14 h-14 mb-4 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0066cc, #5ac8fa)' }}>
                <CloudArrowUpIcon className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-[#333]">Déploiement Rapide</h3>
              <p className="text-[#666666]">Intégration facile grâce à une configuration modulaire qui s'adapte à vos besoins spécifiques.</p>
            </motion.div>

            {/* Formation Minimale */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <div className="w-14 h-14 mb-4 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0066cc, #5ac8fa)' }}>
                <UserGroupIcon className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-[#333]">Formation Minimale</h3>
              <p className="text-[#666666]">Interface intuitive pour une adoption rapide par toutes les équipes, quel que soit leur niveau technique.</p>
            </motion.div>

            {/* Optimisation Continue */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <div className="w-14 h-14 mb-4 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0066cc, #5ac8fa)' }}>
                <ChartBarIcon className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-[#333]">Optimisation Continue</h3>
              <p className="text-[#666666]">Tableaux de bord analytiques pour ajuster vos stratégies et améliorer constamment vos processus.</p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 flex justify-center"
          >
            <img 
              src="/images/kits/cloudmind-adoption.png?v=3"
              alt="Visualisation futuriste et holographique 3D du processus d'adoption de CloudMind" 
              className="w-full max-w-3xl rounded-xl shadow-lg"
            />
          </motion.div>
        </div>
      </section>

      {/* Section CTA */}
      <section className="py-12 bg-gradient-to-r from-blue-600 to-blue-400 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Boostez Vos Projets avec CloudMind</h2>
            <p className="text-xl max-w-2xl mx-auto mb-8">Découvrez comment CloudMind peut transformer votre gestion de projets. Contactez-nous pour une démonstration !</p>
            <div className="flex justify-center">
              <button 
                onClick={() => navigate('/contact')}
                className="px-8 py-3 bg-white text-blue-600 font-medium rounded-full hover:bg-blue-50 transition-colors shadow-lg flex items-center"
              >
                <span>Demander une Démo</span>
                <RocketLaunchIcon className="ml-2 h-5 w-5" />
              </button>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 flex justify-center"
          >
            <img 
              src="/images/kits/cloudmind-cta.png"
              alt="Placeholder DALL-E: Un horizon urbain futuriste avec un logo CloudMind lumineux au-dessus d'une ville connectée, symbolisant l'innovation et la croissance, style néon et technologique" 
              className="w-full max-w-xl rounded-xl shadow-lg float-animation"
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CloudMind; 